import React, { useState } from 'react';

import { Link, addWithConfig } from '@rbilabs/universal-components';
import { DatadogSessionReplayMask } from 'features/observability/rum/datadog-session-replay-mask';
import { StyleSheet } from 'react-native';

import { IDeliveryDriver } from '@rbi-ctg/menu';
import ActionButton, { ActionButtonVariants } from 'components/action-button';
import CloseButton from 'components/close-button';
import { withTypography } from 'components/ui';
import { Box } from 'components/ui/box';
import CourierMarkerSvg from 'pages/order-confirmation/delivery/icons/courier-image';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';

const Heading = withTypography({
  variant: 'headerFour',
  color: primitive.$bbqBrown,
  fontSize: 'xl',
  lineHeight: 'md',
  m: '$0',
  mb: '$4',
  style: { textTransform: 'capitalize' },
});

const CourierName = withTypography({
  fontSize: 'sm',
  mr: '$2',
  numberOfLines: 1,
  style: {
    maxWidth: 80,
  },
});

// TODO: uncomment once make and model become available
// const VehicleInfo = Text.withConfig({
//   fontSize: 'xs',
// });

const ContactPrompt = withTypography({ fontSize: 'xs', color: primitive.$greyOne });

const ContactInfo = Link.withConfig({
  fontSize: 'sm',
  color: primitive.$greyOne,
  isUnderlined: false,
});

const ContactButton = addWithConfig(ActionButton).withConfig({
  maxWidth: '$32',
});

interface ICourierDetailProps {
  driver: IDeliveryDriver;
}

const CourierDetail: React.FC<React.PropsWithChildren<ICourierDetailProps>> = ({ driver }) => {
  const [isContactVisible, setIsContactVisible] = useState<boolean>(false);

  return (
    <>
      <Heading>{t('yourCourier')}</Heading>
      <Box style={styles.main}>
        <Box style={styles.courierInfoContainer}>
          <Box style={styles.courierImg} testID="driver-img">
            {/* TODO: RN - Figure out how we want to handle image url sources not from sanity */}
            {/* <Picture image={driver?.profileImageUrl} alt="courier" /> */}
            <CourierMarkerSvg />
          </Box>
          <Box style={styles.courierInfo}>
            <DatadogSessionReplayMask>
              <CourierName testID="driver-name" data-private>
                {driver?.name || t('noCourierName')}
              </CourierName>
            </DatadogSessionReplayMask>
            {/* {TODO: uncomment this line once make and model become available} */}
            {/* <VehicleInfo>Make & Model</VehicleInfo> */}
          </Box>
        </Box>
        <Box>
          <ContactButton
            testID="driver-contact"
            variant={ActionButtonVariants.OUTLINE}
            fullWidth
            disabled={isContactVisible}
            onPress={() => setIsContactVisible(true)}
          >
            {t('contact')}
          </ContactButton>
        </Box>
      </Box>
      {isContactVisible && (
        <Box style={styles.courierContactInfoContainer}>
          <Box>
            <ContactPrompt>{t('contactCourier')}</ContactPrompt>
            <DatadogSessionReplayMask>
              <ContactInfo href={`tel:${driver?.phoneNumber}`} testID="driver-phone" data-private>
                {driver?.phoneNumber || t('noCourierPhone')}
              </ContactInfo>
            </DatadogSessionReplayMask>
          </Box>
          <Box style={styles.closeButtonWrapper}>
            <CloseButton onPress={() => setIsContactVisible(false)} testID="driver-contact-close" />
          </Box>
        </Box>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  closeButtonWrapper: {
    alignItems: 'center',
    borderRadius: spacing.$6,
    height: spacing.$12,
    width: spacing.$12,
  },
  courierContactInfoContainer: {
    borderTopColor: primitive.$blackOpacity10,
    borderTopWidth: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: spacing.$2,
  },
  courierImg: {
    alignItems: 'center',
    borderRadius: 9999,
    marginRight: spacing.$2,
  },
  courierInfo: {
    justifyContent: 'center',
  },
  courierInfoContainer: {
    flexDirection: 'row',
  },
  main: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.$3_5,
  },
});

export default CourierDetail;
